import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDataSource } from "@/data/DataProvider";
import { usePlan } from "@/hooks/usePlan";
import { screenOf } from "@/lib/activity/screens";
import type { ActivityEvent } from "@/data/types";

const FLUSH_MS = 60_000;

/**
 * 6c.3: each screen she opens goes to her Welthra thread, batched: at most once a minute, and as
 * the page is hidden or closed. Live only: the demo sends nothing.
 */
export function useActivity() {
  const source = useDataSource();
  const { pathname } = useLocation();
  const plan = usePlan();
  const published = Boolean(plan.data?.published);
  const queue = useRef<ActivityEvent[]>([]);
  const last = useRef<string | null>(null);
  const live = source.mode === "live";

  // one event per screen opened (the same screen twice in a row is one visit)
  useEffect(() => {
    if (!live) return;
    const screen = screenOf(pathname, published);
    if (!screen || last.current === pathname) return;
    last.current = pathname;
    queue.current.push({ screen, at: new Date().toISOString() });
  }, [live, pathname, published]);

  useEffect(() => {
    if (!live) return;
    const flush = () => {
      const events = queue.current.splice(0, 50);
      if (events.length) void source.sendActivity(events);
    };
    const timer = window.setInterval(flush, FLUSH_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [live, source]);
}
